import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy } from 'react';
import { subscribeMock } from '@/dev/mockState';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AppShell } from '@/layouts/AppShell';
import { Gated } from '@/features/plans/LockedPage';
import { ThemeProvider } from './providers/Theme';
import { ToastProvider } from './providers/Toast';
import { AuthBoundary } from '@/features/auth/AuthBoundary';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { ScrollRails } from '@/layouts/ScrollRails';

const OverviewPage = lazy(() => import('@/pages/OverviewPage'));
const NewsPage = lazy(() => import('@/pages/NewsPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const WallStreetPage = lazy(() => import('@/pages/WallStreetPage'));
const PortfolioPage = lazy(() => import('@/pages/PortfolioPage'));
const SignalsPage = lazy(() => import('@/pages/SignalsPage'));
const SocialPage = lazy(() => import('@/pages/SocialPage'));
const OrcaAIPage = lazy(() => import('@/pages/OrcaAIPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const AlertsPage = lazy(() => import('@/pages/AlertsPage'));
const WhalesPage = lazy(() => import('@/pages/WhalesPage'));

// Every route exists on every plan. A page that IS a paid feature shows its
// preview when the plan doesn't open it (same check as every other entry point).
const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'news', element: <NewsPage /> },
      { path: 'calendar', element: <Gated feature="economic_calendar"><CalendarPage /></Gated> },
      { path: 'wall-street', element: <Gated feature="wall_street"><WallStreetPage /></Gated> },
      { path: 'portfolio', element: <PortfolioPage /> },
      { path: 'signals', element: <SignalsPage /> },
      { path: 'alerts', element: <Gated feature="price_alerts"><AlertsPage /></Gated> },
      { path: 'whales', element: <Gated feature="whale_alerts"><WhalesPage /></Gated> },
      { path: 'social', element: <Gated feature="twitter"><SocialPage /></Gated> },
      { path: 'ai', element: <OrcaAIPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

// Mock data is local, so it never goes stale; real API will set its own policy.
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: Infinity, retry: false } } });

// DEV: switching a mock scenario reloads page data (skeleton → new state);
// shell data (account, Orca AI, notifications) refreshes in place.
const SHELL = ['account', 'orca-ai', 'notices', 'plans', 'teasers'];
if (import.meta.env.DEV) {
  subscribeMock(() => {
    void queryClient.invalidateQueries({ predicate: (q) => SHELL.includes(String(q.queryKey[0])) });
    void queryClient.resetQueries({ predicate: (q) => !SHELL.includes(String(q.queryKey[0])) });
  });
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <ScrollRails />
            <AuthBoundary>
              <RouterProvider router={router} />
            </AuthBoundary>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
